import { GET_PREFERENCES } from 'src/app/project/modules/preferences/preferences.store';
import { GET_COLLAPSED_GROUPS } from '../site-table/table.ui.store';
import { CALCULATE_COLUMNS, CLEAR_TIMELINE_COLUMNS } from './timeframes/timeline-columns';
import { GET_TIMELINE_COLUMNS } from './timeframes/timeline-timeframes';
import { CLEAR_TIMELINE, GET_TIMELINE, SET_TIMELINE } from './timeline.ui.store';

import { createElement } from 'src/app/core/helpers/dom';
import { checkTableColumnsHover } from '../site-options/site-options';
import { groupTimeline } from '../site-table/grouping/group-timeline';
import { sortTimelinePoints } from '../site-table/sorting/sorting';
import { setTimelineGroupColumnWidth } from './body-elements/resizer-width';

import { TAnyFunction } from '@core/helpers';
import { ApiService } from '@core/http';
import { WindowService } from '@core/services';
import { TTableUser } from '@project/view-models';
import { BehaviorSubject, catchError, Observable, of, Subject, takeUntil } from 'rxjs';
import { TPoint } from 'src/app/project/modules/points/points.model';
import { ERowType } from 'src/app/project/shared/enums/row-type.enum';
import { ECustomFieldType } from '../../custom-fields/custom-field-types-enums';
import { GET_CUSTOM_FIELDS } from '../../custom-fields/custom-fields.store';
import { logErrorInSentry } from '../../errors/response-error';
import { QuickFilterService } from '../../filters/filters-api/search/quick-filter-service';
import { PointSearchService } from '../../points/point-search/point-search.service';
import { SiteOptionsService } from '../site-options/site-options.service';
import { TableLoadingService } from '../site-table/custom-table/table-loading.service';
import { TGroupedPoints } from '../site-table/grouping/group-model';
import { TTimelineItem, TTimelineParams, TUpdatePointCallback } from './timeline.model';
import TimelineBody from './TimelineBody';
import TimelineHead from './TimelineHead';

export default class Timeline {
  workspaceId: string;
  openPointCallback: TAnyFunction;
  highlightPinCallback: TAnyFunction;
  dehighlightPinCallback: TAnyFunction;
  updatePointCallback: TUpdatePointCallback;
  resizing = false;

  visibleColumnIndexes: number[] = [];
  visibleColumnPositions: number[] = [];
  users: TTableUser[] = [];
  private points: TPoint[] = [];
  items: TTimelineItem[] = [];
  width = 0;
  selectedPoints: Set<null> = new Set();
  keyword = '';
  groupedPoints: TGroupedPoints[] = [];
  readonly pointsUpdated$ = new BehaviorSubject<number>(0);
  private readonly destroy$ = new Subject<void>();

  timelineHead: TimelineHead = null;
  timelineBody: TimelineBody = null;
  siteOptionsService: SiteOptionsService;
  tableLoadingService: TableLoadingService;
  pointSearchService: PointSearchService;
  quickFilterService: QuickFilterService;
  private reloadTimeout: any;
  private reloadTimeoutMs = 500;

  element: HTMLElement = null;

  private apiService: ApiService;

  constructor({
    apiService,
    workspaceId: _workspaceId = null,
    openPointCallback: _openPointCallback = null,
    highlightPinCallback: _highlightPinCallback = null,
    dehighlightPinCallback: _dehighlightPinCallback = null,
    updatePointCallback: _updatePointCallback = null,
    siteOptionsService,
    tableLoadingService,
    pointSearchService,
    quickFilterService,
    windowService,
  }: TTimelineParams) {
    this.workspaceId = _workspaceId;
    this.apiService = apiService;
    this.openPointCallback = _openPointCallback;
    this.highlightPinCallback = _highlightPinCallback;
    this.dehighlightPinCallback = _dehighlightPinCallback;
    this.updatePointCallback = _updatePointCallback;
    this.siteOptionsService = siteOptionsService;
    this.tableLoadingService = tableLoadingService;
    this.pointSearchService = pointSearchService;
    this.quickFilterService = quickFilterService;

    setTimelineGroupColumnWidth();

    SET_TIMELINE(this);

    this.element = this.create();

    this.timelineHead = new TimelineHead(this);
    this.timelineBody = new TimelineBody(this, windowService);
    this.load({ keepScrollPosition: false });
    this.reload({ reset: true, timeout: false });
  }

  private create(): HTMLElement {
    return createElement('div', {
      attrs: {
        style: {
          height: '100%',
        },
      },
    });
  }

  switchTimeframe(): void {
    CALCULATE_COLUMNS();

    this.updateWidth();

    this.timelineHead.load(this.apiService, true);
    this.timelineBody.load(true);

    this.setDefaultScrollPosition();
  }

  load({ keepScrollPosition }: { keepScrollPosition: boolean }): void {
    CALCULATE_COLUMNS();

    this.updateVisibleColumnIndexes();
    this.updateVisibleColumnPositions();
    this.updateWidth();

    if (!keepScrollPosition) {
      this.setDefaultScrollPosition();
    }

    this.timelineHead.load(this.apiService, true);
    this.timelineBody.load(true);

    this.timelineHead.scrollLeft(this.timelineBody.virtualScroller.scrollElement.scrollLeft);
  }

  private setDefaultScrollPosition(): void {
    const today = new Date().getTime();
    const timelineData = GET_TIMELINE_COLUMNS();
    let todayIndex = 0;

    for (let i = 1; i < timelineData.timeframes.length; i++) {
      if (timelineData.timeframes[i - 1] < today && timelineData.timeframes[i] >= today) {
        todayIndex = i;

        break;
      }
    }

    this.timelineBody.virtualScroller.disableSmoothScrolling();

    const newPosition =
      todayIndex * timelineData.bottomWidth - this.timelineBody.virtualScroller.viewportWidth * 0.2;

    this.timelineBody.virtualScroller.scrollLeft(newPosition);
    this.timelineBody.virtualScroller.enableSmoothScrolling();
  }

  clear(): void {
    this.updateWidth(0);
    this.timelineHead.clear();
    this.timelineBody.clear();
    CLEAR_TIMELINE_COLUMNS();
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
      this.reloadTimeout = null;
    }

    this.clear();

    this.timelineHead?.destroy();
    this.timelineBody?.destroy();
    this.timelineHead = null;
    this.timelineBody = null;

    this.pointsUpdated$.complete();

    if (GET_TIMELINE() === this) {
      CLEAR_TIMELINE();
    }

    this.element = null;
  }

  updateWidth(_width?: number): void {
    const headerData = GET_TIMELINE_COLUMNS();

    if (_width === undefined && headerData) {
      this.width = headerData.bottomElements.length * headerData.bottomWidth;
    } else {
      this.width = _width;
    }
  }

  sortTimeline(): void {
    this.sortPoints();
    this.generateItems();
    this.timelineBody.virtualScroller.load(this.items, true);
  }

  updateColumns(): void {
    this.timelineBody.updateColumns();
  }

  updateVisibleColumnIndexes(): void {
    this.visibleColumnIndexes.length = 0;
    this.visibleColumnIndexes = GET_TIMELINE_COLUMNS().bottomElements.map(
      (_column, _index) => _index,
    );

    checkTableColumnsHover();
  }

  updateVisibleColumnPositions(): void {
    const data = GET_TIMELINE_COLUMNS();
    this.visibleColumnPositions.length = 0;

    this.visibleColumnIndexes.forEach((_columnIndex, _index) => {
      if (_index === 0) {
        if (this.visibleColumnIndexes.length > 1) {
          this.visibleColumnPositions[_index + 1] = data.bottomWidth;
        }

        this.visibleColumnPositions[_index] = 0;
      } else if (this.visibleColumnIndexes.length > _index + 1) {
        if (this.visibleColumnIndexes.length > _index + 1) {
          this.visibleColumnPositions[_index + 1] =
            this.visibleColumnPositions[_index] + data.bottomWidth;
        }
      }
    });
  }

  updatePoints(_points: TPoint[]): void {
    this.points = this.filterPointsWithTimelineProperty(_points);
    this.generateItems();
    this.pointsUpdated$.next(this.points.length);
  }

  addPoint(_point: TPoint): void {
    this.points.push(_point);
    this.sortTimeline();
  }

  sortPoints(): void {
    this.points.sort(sortTimelinePoints());

    const sortButton = this.siteOptionsService.getSortButton();

    if (sortButton) {
      sortButton.update({ updateTable: false });
    }
  }

  loadAvatars(_users: TTableUser[]): void {
    this.users = _users;

    this.timelineBody.load(true);
  }

  setKeyword(_keyword: string): void {
    this.keyword = _keyword;
  }

  filterPointsWithTimelineProperty(points: TPoint[]): TPoint[] {
    const customFields = GET_CUSTOM_FIELDS();
    const timelineFieldIds = Object.values(customFields)
      .filter((field) => field.type === ECustomFieldType.TIMELINE)
      .map((field) => field.id);

    return points.filter((point) => {
      if (!point.customFieldsMap) {
        return false;
      }

      return timelineFieldIds.some((fieldId) => {
        const customField = point.customFieldsMap[fieldId];
        return customField && !!customField.value;
      });
    });
  }

  generateItems(): void {
    const collapsedGroups = GET_COLLAPSED_GROUPS();
    const preferences = GET_PREFERENCES();

    this.items.length = 0;

    if (preferences.timeline?.group) {
      this.groupedPoints = groupTimeline(this.points);

      if (window.innerWidth >= 901) {
        this.timelineBody.virtualScroller.updateExtraWidth(30);
      } else {
        this.timelineBody.virtualScroller.updateExtraWidth(0);
      }

      this.timelineBody.virtualScroller.updateFrozenColumnWidth(46);
      this.element.classList.add('table__body--grouped');

      this.items = this.groupedPoints.flatMap((_group, _groupIndex) => {
        const points = [];
        const collapsed = collapsedGroups.has(_group.id);

        points.push({
          type: ERowType.HEADER,
          context: _group.value,
          id: _group.id,
        });

        if (!collapsed) {
          points.push(
            ..._group.points.map((_point) => ({
              type: ERowType.POINT,
              index: _point,
            })),
          );
        }

        return points;
      });
    } else {
      this.timelineBody.virtualScroller.updateExtraWidth(0);
      this.timelineBody.virtualScroller.updateFrozenColumnWidth(0);
      this.element.classList.remove('table__body--grouped');

      if (this.points.length === 0) {
        this.items = [
          {
            type: ERowType.EMPTY_MESSAGE,
            index: 0,
          },
        ];
      } else {
        this.items = this.points.map((_point, _index) => ({
          type: ERowType.POINT,
          index: _index,
        }));
      }
    }

    this.updateWidth();
  }

  setResizing(resizing: boolean): void {
    this.resizing = resizing;
  }

  getPoints(): TPoint[] {
    return this.points;
  }

  reload({
    reset,
    timeout,
    preserveTableState,
  }: {
    reset: boolean;
    timeout: boolean;
    preserveTableState?: boolean;
  }): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }

    this.tableLoadingService.toggleSpinner(true);

    this.reloadTimeout = setTimeout(
      () => {
        this.pointSearchService
          .searchPointsWithPagination({
            reset,
            isTimeline: true,
            preserveTableState,
          })
          .pipe(
            takeUntil(this.destroy$),
            catchError((error) => this.handlePaginationError(error)),
          )
          .subscribe((points) => {
            this.updatePoints(points);
            this.load({ keepScrollPosition: true });
            this.tableLoadingService.toggleSpinner(false);
          });
      },
      timeout ? this.reloadTimeoutMs : 0,
    );
  }

  private handlePaginationError(error: any): Observable<null> {
    this.tableLoadingService.toggleSpinner(false);
    this.tableLoadingService.showLoadingPointsError();
    this.load({ keepScrollPosition: true });
    logErrorInSentry({ message: `Error in Timeline Pagination: ${error}`, error });
    return of(null);
  }
}
